import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../context/toastContext";
import useEventStore from "../stores/events/event";

const Badge = ({ children }) => (
  <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
    {children}
  </span>
);

export default function EventManagement() {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const events = useEventStore((s) => s.events);
  const loadingList = useEventStore((s) => s.loadingList);
  const loadingAction = useEventStore((s) => s.loadingAction);
  const fetchIndex = useEventStore((s) => s.actions.fetchIndex);
  const deleteEvent = useEventStore((s) => s.actions.deleteEvent);

  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchIndex();
  }, [fetchIndex]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return events;
    return events.filter((e) => {
      const t = (e.title || "").toLowerCase();
      const s = (e.slug || "").toLowerCase();
      return t.includes(q) || s.includes(q);
    });
  }, [events, search]);

  const onDelete = async (slug) => {
    if (!window.confirm(`Delete event "${slug}"?`)) return;
    const ok = await deleteEvent(slug);
    if (ok) {
      addToast("success", "Event deleted.");
      fetchIndex();
    } else {
      addToast("error", "Failed to delete event.");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50">
      <div className="max-w-screen-2xl mx-auto px-6 py-8">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Events</h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage event pages and popup banners.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => fetchIndex()}
                className="px-5 py-2.5 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50"
                disabled={loadingList}
              >
                {loadingList ? "Refreshing..." : "Refresh"}
              </button>

              <button
                onClick={() => navigate("/events/create")}
                className="px-6 py-2.5 rounded-xl text-white font-semibold bg-blue-600 hover:bg-blue-700"
              >
                + Create Event
              </button>
            </div>
          </div>

          <div className="mt-5">
            <input
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
              placeholder="Search by title or slug..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
            <h2 className="text-left text-lg font-bold text-gray-900">
              {filtered.length} event(s)
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-6 py-3 font-semibold text-gray-700">
                    Title
                  </th>
                  <th className="text-left px-6 py-3 font-semibold text-gray-700">
                    Slug
                  </th>
                  <th className="text-left px-6 py-3 font-semibold text-gray-700">
                    Published
                  </th>
                  <th className="text-left px-6 py-3 font-semibold text-gray-700">
                    Popup
                  </th>
                  <th className="text-right px-6 py-3 font-semibold text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {loadingList ? (
                  <tr>
                    <td className="px-6 py-6 text-gray-500" colSpan={5}>
                      Loading...
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td className="px-6 py-6 text-gray-500" colSpan={5}>
                      No events found.
                    </td>
                  </tr>
                ) : (
                  filtered.map((e) => (
                    <tr key={e.slug} className="hover:bg-gray-50/60">
                      <td className="px-6 py-4 font-semibold text-gray-900">
                        {e.title || "-"}
                      </td>
                      <td className="px-6 py-4 font-mono text-gray-700">
                        {e.slug}
                      </td>
                      <td className="px-6 py-4">
                        <Badge>{e.is_published ? "Yes" : "No"}</Badge>
                      </td>
                      <td className="px-6 py-4">
                        <Badge>{e.popup_enabled ? "Enabled" : "Off"}</Badge>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-end gap-2">
                          <button
                            // onClick={() => window.open(`/events/view/${e.slug}`, "_blank")}
                            onClick={() => navigate(`/events/view/${e.slug}`)}
                            className="px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50"
                          >
                            View
                          </button>
                          <button
                            onClick={() => navigate(`/events/edit/${e.slug}`)}
                            className="px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => onDelete(e.slug)}
                            disabled={loadingAction}
                            className="px-3 py-2 rounded-lg border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-60"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
